/**
 * Server-side helper to get image URL with CDN support
 * Ensures SSR and client-side render the same URL (prevents hydration errors)
 */

import { getMediaSettingsServer } from './get-media-settings-server';
import { resolveImagePreset } from './image-presets';
import { API_BASE_URL } from './config';
import { normalizeMediaPath } from './utils';
import { extractRelativeMediaPath } from './sync-media-caption';
import { buildImageCdnUrl } from './buildImageCdnUrl';
import { getMediaUrl } from './media';

/**
 * Get image URL for server-side rendering.
 * CDN domain + boyutlar admin `/admin/settings` üzerinden gelir.
 */
export async function getImageUrlServer(
  image: string | null | undefined,
  size?: string,
): Promise<string> {
  if (typeof image === 'object' && image !== null) {
    return getMediaUrl(image, size as any, false);
  }

  const normalized = normalizeMediaPath(image);
  if (!normalized) {
    return '';
  }

  const imagePath = normalized;

  // haberapi.test/storage/icerik/... → göreli path, sonra CDN
  if (
    imagePath.startsWith('http://') ||
    imagePath.startsWith('https://')
  ) {
    const relative = extractRelativeMediaPath(imagePath);
    if (relative) {
      return getImageUrlServer(relative, size);
    }
    return imagePath;
  }

  if (imagePath.startsWith('data:') || imagePath.startsWith('blob:')) {
    return imagePath;
  }

  let cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
  cleanPath = cleanPath.startsWith('storage/') ? cleanPath.substring(8) : cleanPath;

  if (!cleanPath || cleanPath.trim() === '') {
    return '';
  }

  const normalizedPath = cleanPath.replace(/^\/+/, '');
  const settings = await getMediaSettingsServer();
  const options = resolveImagePreset(size, settings?.presets);

  if (settings?.providers?.s3?.publicBaseUrl?.trim()) {
    const baseUrl = settings.providers.s3.publicBaseUrl.replace(/\/$/, '');
    const domain = baseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    return buildImageCdnUrl(normalizedPath, options, domain);
  }

  const needsTransform =
    size &&
    (options.width !== undefined ||
      options.height !== undefined ||
      (options.format && !['jpeg', 'jpg', 'png'].includes(options.format)));

  if (needsTransform) {
    return buildImageCdnUrl(normalizedPath, options, undefined, undefined, {
      apiBaseUrl: API_BASE_URL,
    });
  }

  const storageBase = API_BASE_URL.replace(/\/$/, '');
  return `${storageBase}/storage/${normalizedPath}`;
}
